import logger from './logger';

// Responsible for ensuring the cable connection is in good health by validating the heartbeat pings sent from the server, and attempting
// revival reconnections if things go astray. Internal class, not intended for direct user manipulation.

const now = () => new Date().getTime();

const secondsSince = (time: number) => (now() - time) / 1000;

export default class ConnectionMonitor
{
    readonly staleThreshold: number;
    readonly reconnectionBackoffRate: number;
    connection: any;
    reconnectAttempts: number;
    startedAt!: number;
    stoppedAt: any;
    pingedAt!: number;
    disconnectedAt: any;
    pollTimeout: any;

    /**
     * Constructor
     */
    constructor(connection: any)
    {
        this.visibilityDidChange     = this.visibilityDidChange.bind(this);
        this.connection              = connection;
        this.reconnectAttempts       = 0;
        this.staleThreshold          = 6; // Server::Connections::BEAT_INTERVAL * 2 (missed two pings)
        this.reconnectionBackoffRate = 0.15;
    }

    get refreshedAt()
    {
        return this.pingedAt ? this.pingedAt : this.startedAt;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    start(): void
    {
        if (!this.isRunning())
        {
            this.startedAt = now();
            delete this.stoppedAt;
            this.startPolling();
            addEventListener('visibilitychange', this.visibilityDidChange);
            logger.log(`ConnectionMonitor started. stale threshold = ${this.staleThreshold} s`);
        }
    }

    stop(): void
    {
        if (this.isRunning())
        {
            this.stoppedAt = now();
            this.stopPolling();
            removeEventListener('visibilitychange', this.visibilityDidChange);
            logger.log('ConnectionMonitor stopped');
        }
    }

    isRunning(): boolean
    {
        return Boolean(this.startedAt && !this.stoppedAt);
    }

    recordPing(): void
    {
        this.pingedAt = now();
    }

    recordConnect(): void
    {
        this.reconnectAttempts = 0;
        this.recordPing();
        delete this.disconnectedAt;
        logger.log('ConnectionMonitor recorded connect');
    }

    recordDisconnect(): void
    {
        this.disconnectedAt = now();
        logger.log('ConnectionMonitor recorded disconnect');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private startPolling(): void
    {
        this.stopPolling();
        this.poll();
    }

    private stopPolling(): void
    {
        clearTimeout(this.pollTimeout);
    }

    private poll(): void
    {
        this.pollTimeout = setTimeout(() =>
            {
                this.reconnectIfStale();
                this.poll();
            }
            , this.getPollInterval());
    }

    private getPollInterval(): number
    {
        const { staleThreshold, reconnectionBackoffRate } = this;
        const backoff                                     = Math.pow(1 + reconnectionBackoffRate, Math.min(this.reconnectAttempts, 10));
        const jitterMax                                   = this.reconnectAttempts === 0 ? 1.0 : reconnectionBackoffRate;
        const jitter                                      = jitterMax * Math.random();
        return staleThreshold * 1000 * backoff * (1 + jitter);
    }

    private reconnectIfStale(): void
    {
        if (this.connectionIsStale())
        {
            logger.log(`ConnectionMonitor detected stale connection. reconnectAttempts = ${this.reconnectAttempts}, time stale = ${secondsSince(this.refreshedAt)} s, stale threshold = ${this.staleThreshold} s`);
            this.reconnectAttempts++;
            if (this.disconnectedRecently())
            {
                logger.log(`ConnectionMonitor skipping reopening recent disconnect. time disconnected = ${secondsSince(this.disconnectedAt)} s`);
            }
            else
            {
                logger.log('ConnectionMonitor reopening');
                this.connection.reopen();
            }
        }
    }

    private connectionIsStale(): boolean
    {
        return secondsSince(this.refreshedAt) > this.staleThreshold;
    }

    private disconnectedRecently(): boolean
    {
        return this.disconnectedAt && (secondsSince(this.disconnectedAt) < this.staleThreshold);
    }

    private visibilityDidChange(): void
    {
        if (document.visibilityState === 'visible')
        {
            setTimeout(() =>
                {
                    if (this.connectionIsStale() || !this.connection.isOpen())
                    {
                        logger.log(`ConnectionMonitor reopening stale connection on visibilitychange. visibilityState = ${document.visibilityState}`);
                        this.connection.reopen();
                    }
                }
                , 200);
        }
    }
}


